
    import { Component, Vue, Inject, Prop, Watch } from 'vue-property-decorator';
    import Util from '@/lib/util';
    import AbpBase from '@/lib/abpbase';
    import DateUtils from '@/common/js/DateUtils';
    import Enums from '@/lib/enums';
    import { routers } from '@/router/router';
    import Ajax from '@/lib/ajax';
    import PageRequest from '@/store/entities/page-request';
    import PageResult from '@/store/entities/page-result';
    import storeaddressInfo from '@/store/entities/back-storeaddress-list'

    interface StoreInfo {
        id: number;
        storeName: string;
    }

    interface OperationRecord {
        storeName: string;
        orderNumber: string;
        time: Date;
        success: boolean;
        message?: string;
    }

    interface Statistics {
        total: number;
        success: number;
        failure: number;
        processedOrders: Set<string>; // 用于记录已经处理过的运单号
    }

    @Component({
        name: "outboundConfirm"
    })
    export default class OutboundConfirm extends AbpBase {
        // 表单数据
        formData = {
            storeIds: [] as number[],
            orderNumber: ''
        };

        // 仓库列表
        storeList: StoreInfo[] = [];

        // 加载状态
        loading = false;

        // 结果信息
        resultMessage = '';
        resultType: 'success' | 'error' | 'warning' | 'info' = 'info';

        // 最近操作记录
        recentOperations: OperationRecord[] = [];

        // 统计信息
        statistics: Statistics = {
            total: 0,
            success: 0,
            failure: 0,
            processedOrders: new Set<string>()
        };

        // 操作记录表格列
        operationColumns = [
            {
                title: '运单号',
                key: 'orderNumber',
                width: 100,
                render: (h: any, params: any) => {
                    return h('div', [
                        h('span', {
                            style: {
                                fontWeight: 'bold',
                                color: params.row.success ? '#19be6b' : '#ed4014'
                            }
                        }, params.row.orderNumber)
                    ]);
                }
            },
            {
                title: '时间',
                key: 'time',
                width: 100,
                align: 'right',
                render: (h: any, params: any) => {
                    const timeStr = this.formatTimeToHHMMSS(params.row.time);
                    return h('div', [
                        h('span', {
                            style: {
                                color: params.row.success ? '#19be6b' : '#ed4014',
                                fontSize: '12px'
                            }
                        }, timeStr)
                    ]);
                }
            }
        ];

        // 从本地存储加载统计信息
        loadStatistics() {
            try {
                const savedStats = localStorage.getItem('outbound_statistics');
                if (savedStats) {
                    const stats = JSON.parse(savedStats);
                    this.statistics.total = stats.total || 0;
                    this.statistics.success = stats.success || 0;
                    this.statistics.failure = stats.failure || 0;
                    this.statistics.processedOrders = new Set(stats.processedOrders || []);
                }
            } catch (error) {
                console.error('加载统计信息失败:', error);
                // 如果加载失败，重置为默认值
                this.resetStatistics();
            }
        }

        // 保存统计信息到本地存储
        saveStatistics() {
            try {
                const statsToSave = {
                    total: this.statistics.total,
                    success: this.statistics.success,
                    failure: this.statistics.failure,
                    processedOrders: Array.from(this.statistics.processedOrders)
                };
                localStorage.setItem('outbound_statistics', JSON.stringify(statsToSave));
            } catch (error) {
                console.error('保存统计信息失败:', error);
            }
        }

        // 重置统计信息
        resetStatistics() {
            this.$Modal.confirm({
                title: '确认重置统计',
                content: '确定要重置出仓确认的统计信息吗？',
                onOk: () => {
                    this.statistics = {
                        total: 0,
                        success: 0,
                        failure: 0,
                        processedOrders: new Set<string>()
                    };
                    this.saveStatistics();
                    this.$Message.success('统计信息已重置');
                }
            });
        }

        // 更新统计信息
        updateStatistics(orderNumber: string, success: boolean) {
            // 检查这个运单号是否已经处理过
            if (this.statistics.processedOrders.has(orderNumber)) {
                // 如果已经处理过，不重复统计
                return false;
            }

            // 添加到已处理集合
            this.statistics.processedOrders.add(orderNumber);

            // 更新统计数字
            this.statistics.total++;
            if (success) {
                this.statistics.success++;
            } else {
                this.statistics.failure++;
            }

            // 保存到本地存储
            this.saveStatistics();

            return true;
        }

        // 格式化时间为 HH:mm:ss
        formatTimeToHHMMSS(time: Date): string {
            if (!time) return '';

            const date = new Date(time);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const seconds = date.getSeconds().toString().padStart(2, '0');

            return `${hours}:${minutes}:${seconds}`;
        }

        // 仓库选择变化
        onStoreChange(value: number[]) {
            console.log('选中仓库IDs:', value);
        }

        focusInput() {
            this.$nextTick(() => {
                const inputElement = this.$refs.orderNumberInput as HTMLInputElement;
                if (inputElement) {
                    inputElement.focus();
                }
            });
        }

        // 出仓确认
        async confirmOutbound() {
            // 验证输入
            if (!this.formData.storeIds || this.formData.storeIds.length === 0) {
                this.showMessage('请选择至少一个仓库', 'warning');
                return;
            }

            if (!this.formData.orderNumber.trim()) {
                this.showMessage('请输入运单号', 'warning');
                (this.$refs.orderNumberInput as any).focus();
                return;
            }

            const orderNumber = this.formData.orderNumber.trim();
            this.loading = true;
            this.resultMessage = '';

            try {
                // 将仓库ID数组转换为逗号分隔的字符串
                const storeIdsString = this.formData.storeIds.join(',');

                await this.$store.dispatch({
                    type: 'backstockorder/confirmArriveOrder',
                    data: {
                        storeId: storeIdsString,
                        orderNumber: orderNumber
                    }
                });

                // 显示成功消息
                this.resultMessage = `${orderNumber} 出仓成功`;
                this.resultType = 'success';

                // 更新统计信息
                const isNewRecord = this.updateStatistics(orderNumber, true);

                // 添加操作记录
                this.addOperationRecord(true, orderNumber);

                // 清空输入并聚焦
                setTimeout(() => {
                    this.clearInput();
                }, 100);

            } catch (error: any) {
                // 🔧 这里手动解析错误信息
                let errorMessage = '出仓失败';

                // 尝试从错误对象中获取详细信息
                if (error.response && error.response.data) {
                    // 检查ABP框架的错误格式
                    if (error.response.data.error) {
                        errorMessage = error.response.data.error.message || errorMessage;
                        if (error.response.data.error.details) {
                            errorMessage += ': ' + error.response.data.error.details;
                        }
                    }
                    // 检查常规错误格式
                    else if (error.response.data.Message) {
                        errorMessage = error.response.data.Message;
                    }
                    // 纯文本错误
                    else if (typeof error.response.data === 'string') {
                        errorMessage = error.response.data;
                    }
                }
                // 如果拦截器返回了处理过的错误对象
                else if (error.message) {
                    errorMessage = error.message;
                }

                // 显示错误消息（不显示500，显示实际错误）
                this.resultMessage = `出仓失败: ${errorMessage}`;
                this.resultType = 'error';

                // 更新统计信息
                this.updateStatistics(orderNumber, false);

                // 添加操作记录
                this.addOperationRecord(false, orderNumber, errorMessage);
            } finally {
                this.loading = false;
            }
        }

        // 清空输入
        clearInput() {
            this.formData.orderNumber = '';
            this.resultMessage = '';
            this.$nextTick(() => {
                const input = this.$refs.orderNumberInput as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            });
        }

        // 显示消息
        showMessage(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') {
            this.resultMessage = message;
            this.resultType = type;
        }

        // 添加操作记录
        addOperationRecord(success: boolean, orderNumber?: string, message?: string) {
            // 获取选中的仓库名称
            const selectedStoreNames = this.storeList
                .filter(store => this.formData.storeIds.includes(store.id))
                .map(store => store.storeName);

            const record: OperationRecord = {
                storeName: selectedStoreNames.length > 0
                    ? selectedStoreNames.join(', ')
                    : '未选择仓库',
                orderNumber: orderNumber || this.formData.orderNumber,
                time: new Date(),
                success,
                message
            };

            // 添加到列表开头，最多保留10条记录
            this.recentOperations.unshift(record);
            if (this.recentOperations.length > 10) {
                this.recentOperations.pop();
            }
        }

        // 加载仓库列表
        async loadStoreList() {
            await this.$store.dispatch({
                type: 'backstoreaddress/getAll',
                data: {}
            });

            this.storeList = this.$store.state.backstoreaddress.list.filter(function (item) {
                return item.whetherEffective == true
            });

            if (this.storeList.length == 1) {
                this.formData.storeIds = [this.storeList[0].id];
            } else {
                this.formData.storeIds = [];
            }
        }

        // 组件创建时
        async created() {
            // 加载统计信息
            this.loadStatistics();

            // 加载仓库列表
            await this.loadStoreList();

            // 自动聚焦到输入框
            this.$nextTick(() => {
                const input = this.$refs.orderNumberInput as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            });

            // 隐藏菜单（如果是移动端）
            if (abp.isMobile()) {
                abp.hideMenu();
            }
        }

        // 组件挂载后
        mounted() {
            // 确保输入框获得焦点
            this.$nextTick(() => {
                const input = this.$refs.orderNumberInput as HTMLInputElement;
                if (input) {
                    input.focus();
                }
            });
        }
    }
